import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {List} from '@tickchak/react-share/app';
import useShow from '../../../../../hooks/useShow';
import useCloseOnScroll from '../../../../../hooks/useCloseOnScroll';
import {TTimetableItem} from '../../../../../redux/timetable/timetable.type';
import {deleteLecture, duplicateLecture} from '../../../../../redux/timetable/timetableActions';
import copyToClipboard from '../../../../../library/copyToClipboard';
import {bounceMessage} from '../../../../../layout/userMessages/UserMessage';
import KebabDots from '../../../../../assets/icons/KebabDots';
import XIcon from '../../../../../assets/icons/XIcon';
import EditIcon from '../../../../../assets/icons/EditIcon';
import DuplicateIcon from '../../../../../assets/icons/DuplicateIcon';
import TrashIcon from '../../../../../assets/icons/TrashIcon';
import CopyNumberIcon from '../../../../../assets/icons/CopyNumberIcon';
import * as css from './LectureCardMenu.css';

interface LectureCardMenuProps {
    lecture: TTimetableItem;
    onEdit?: () => void;
}
const DROPDOWN_WIDTH = 278;

function LectureCardMenu({
    lecture,
    onEdit = () => {},
}: LectureCardMenuProps) {
    const {t} = useTranslation('timetable');
    const show = useShow();
    const triggerRef = useRef<HTMLButtonElement>(null);
    useCloseOnScroll(show.isOpen, show.close);

    const getMenuPosition = () => {
        if(!triggerRef.current) return undefined;
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.top;
        return spaceBelow < 300
            ? {bottom: window.innerHeight - rect.top, left: rect.right}
            : {top: rect.top - 13, left: rect.right};
    };

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        show.isOpen ? show.close() : show.open();
    };

    const handleItemClick = ({id}: {id: string | number}) => {
        if(id === 'edit') onEdit();
        else if(id === 'duplicate') duplicateLecture(lecture.ttid);
        else if(id === 'delete') deleteLecture(lecture.ttid);
        else if(id === 'copyId') { copyToClipboard(String(lecture.ttid)); bounceMessage(t('lectureCopied'), 'success'); }
        show.close();
    };

    return (
        <>
            <css.trigger ref={triggerRef} onClick={toggle} aria-label={t('lectureActions')}>
                <KebabDots />
            </css.trigger>
            {show.isOpen && (
                <List
                    fixedPosition={getMenuPosition()}
                    show={show}
                    onItemClick={handleItemClick}
                    position={{width: `${DROPDOWN_WIDTH}px`, height: 'fit-content'}}
                >
                    <List.Items>
                        <List.Item.Item id="edit"      text={t('editLecture')}      endIcon={<EditIcon />} />
                        <List.Item.Item id="duplicate" text={t('duplicateLecture')} endIcon={<DuplicateIcon />} />
                        <List.Item.Item id="delete"    text={t('deleteLecture')}    endIcon={<TrashIcon />} />
                        <List.Item.Item id="copyId"    text={`${t('copyLectureId')}: ${lecture.ttid}`} endIcon={<CopyNumberIcon />} />
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('close')} iconStart={<XIcon />} size="category" />
                    </List.Footer>
                </List>
            )}
        </>
    );
}

export default LectureCardMenu;
